import { useState, useCallback } from 'react';
import api from '../services/api';

/**
 * Custom hook for managing chat messages
 * Handles message state, history loading, and message operations
 * 
 * @param {string} campaignId - Campaign ID
 * @returns {Object} - Message state and operations
 */
const useChatMessages = (campaignId) => {
  const [messages, setMessages] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);

  /**
   * Load chat history from API
   * 
   * @param {boolean} skipDataFetch - Whether to skip triggering data fetch (optional)
   * @returns {Promise<Array>} - Array of messages
   */
  const loadChatHistory = useCallback(async (skipDataFetch = false) => {
    if (!campaignId) {
      setMessages([]);
      setChatHistory([]);
      return [];
    }

    try {
      const response = await api.get(`/api/chat/sessions/${campaignId}/messages`);
      const historyMessages = response.data.messages || [];
      
      // Transform messages to include canRevert flag
      const transformedMessages = historyMessages.map((msg, index) => {
        return {
          ...msg,
          id: index, // Use array index as ID - stable and corresponds to backend order
          timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
          canRevert: msg.role === 'user' && msg.version_id,
          versionId: msg.version_id // version_id is at top level, not in metadata
        };
      });
      
      // Smart update: only replace messages if we have equal or more messages than before
      // This prevents clearing optimistic messages that haven't been saved to backend yet
      setMessages(prev => {
        // If backend has more or equal messages, use backend state
        if (transformedMessages.length >= prev.length) {
          return transformedMessages;
        }
        // If backend has fewer messages, keep existing messages (backend might not be synced yet)
        console.warn('[useChatMessages] Backend has fewer messages than local state, keeping local messages', {
          backend: transformedMessages.length,
          local: prev.length
        });
        return prev;
      });
      setChatHistory(transformedMessages.filter(msg => msg.canRevert));
      
      return transformedMessages;
    } catch (error) {
      console.error('Error loading chat history:', error);
      // Don't clear messages on error - keep existing optimistic messages in UI
      // This prevents messages from disappearing when backend hasn't saved them yet
      // or when there's a temporary API error
      return [];
    }
  }, [campaignId]);

  /**
   * Add a new message to the chat
   * 
   * @param {string} role - Message role (user, assistant, system)
   * @param {string} content - Message content
   * @param {Object} options - Additional options
   * @param {boolean} options.isLoading - Whether message is loading
   * @param {boolean} options.canRevert - Whether message can be reverted
   * @param {string} options.versionId - Version ID for revertible messages
   * @param {boolean} options.isStreaming - Whether message is streaming
   * @returns {number} - Index of the new message
   */
  const addMessage = useCallback((role, content, options = {}) => {
    const {
      isLoading = false,
      canRevert = false,
      versionId = null,
      isStreaming = false
    } = options;

    let messageId;
    setMessages(prev => {
      messageId = prev.length;
      const newMessage = {
        id: messageId,
        role,
        content,
        timestamp: new Date(),
        isLoading,
        canRevert,
        versionId,
        isStreaming
      };
      return [...prev, newMessage];
    });
    
    return messageId;
  }, []);

  /**
   * Update an existing message
   * 
   * @param {number} messageId - Message ID to update
   * @param {Object} updates - Updates to apply
   */
  const updateMessage = useCallback((messageId, updates) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId ? { ...msg, ...updates } : msg
    ));
  }, []);

  /**
   * Remove messages after a certain index (for edit scenarios)
   * 
   * @param {number} afterIndex - Remove all messages after this index
   */
  const removeMessagesAfter = useCallback((afterIndex) => {
    setMessages(prev => prev.slice(0, afterIndex + 1));
  }, []);

  /**
   * Clear all messages
   */
  const clearMessages = useCallback(() => {
    setMessages([]);
    setChatHistory([]);
  }, []);

  return {
    messages,
    chatHistory,
    setMessages,
    loadChatHistory,
    addMessage,
    updateMessage,
    removeMessagesAfter,
    clearMessages,
  };
};

export default useChatMessages;

